/**
 * `MentionChip` — a referenced-workspace-file chip. Used in TWO places:
 *
 *  1. The composer (above the textarea, beside the selection chip) — with
 *     `onRemove`, rendering a dismiss × so the lawyer can drop the
 *     reference before sending.
 *  2. The user-message echo (below the sent bubble in `ChatMessageList`) —
 *     with `onOpen`, rendering the chip as a button that opens the file in
 *     the workspace pane (analogue of the selection echo sub-line).
 *
 * Gold accent (`border-gold-500/40 bg-gold-400/10 text-gold-700`) — the
 * same palette as the Reavaliar button + the selection chip, so the lawyer
 * reads "this is a Lexiun-internal reference" consistently. Filename is
 * middle-ellipsized (`truncateFilename`) so long case-doc names stay
 * compact; `title={path}` exposes the full case-root-relative path on hover.
 */

import { Paperclip, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { truncateFilename } from "@/lib/workspace/mentions-directive";

interface MentionChipProps {
  name: string;
  /** Case-root-relative path — used by `onOpen`; NEVER shown (rule 6). */
  path: string;
  /** Workspace-file-tags: the `@tag` handle (no `@`) — labels the chip tag-first (A8). */
  tag?: string;
  /** Workspace-file-tags: the friendly title — the chip label when untagged + the hover tooltip. */
  title?: string;
  /** Composer use — renders the dismiss × that removes the chip. */
  onRemove?: () => void;
  /** Echo use — makes the chip body a button that opens the file; receives
   *  the clicked element as the open animation's anchor. */
  onOpen?: (anchorEl: HTMLElement) => void;
}

export function MentionChip({ name, path, tag, title, onRemove, onOpen }: MentionChipProps) {
  const { t } = useTranslation();
  // TAG-FIRST label (A8): the tag (no `@`) → title → the (middle-ellipsized) basename.
  const label = tag ? tag.trim() : title?.trim() ? title.trim() : truncateFilename(name, 32);
  // Rule 6: the hover tooltip is the FRIENDLY name, never the OS path/name.
  const hoverTitle = title?.trim() || (tag ? tag.trim() : undefined);

  const body = (
    <>
      <Paperclip
        size={11}
        className="shrink-0 text-gold-700 dark:text-gold-400"
        aria-hidden
      />
      <span className={cn("min-w-0 truncate", tag && "font-mono")}>{label}</span>
    </>
  );

  return (
    <span
      data-testid="composer-mention-chip"
      data-path={path}
      title={hoverTitle}
      className={cn(
        "inline-flex max-w-[14rem] items-center gap-1 rounded-md border px-2 py-0.5 text-caption",
        "border-gold-500/40 bg-gold-400/10 text-gold-700 dark:text-gold-400",
      )}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={(e) => onOpen(e.currentTarget)}
          className="inline-flex min-w-0 items-center gap-1 rounded transition-colors hover:text-coffee-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:hover:text-cream-100"
        >
          {body}
        </button>
      ) : (
        <span className="inline-flex min-w-0 items-center gap-1">{body}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          data-testid="composer-mention-chip-remove"
          aria-label={t("composer.mentions.removeAria")}
          title={t("composer.mentions.removeAria")}
          className="shrink-0 rounded text-gold-700 transition-colors hover:bg-gold-100 hover:text-coffee-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-gold-400 dark:hover:bg-gold-900/40 dark:hover:text-cream-100"
        >
          <X size={11} />
        </button>
      )}
    </span>
  );
}
